import { useCallback, useContext, useMemo } from 'react'
import { IssueGridCols } from '../components/entities/IssuesGrid'
import { UserMeContext } from '../contexts/UserMeContext'

export type IssuesGridColumnSettingsName = 'current' | 'previous' | 'future'

const defaultValues: Array<{
    availableIn: Array<IssuesGridColumnSettingsName>
    name: Exclude<IssueGridCols, 'issueType' | 'shareName' | 'tags' | 'status' | 'noteIndicator' | 'updatedAt'>
    visibleIn: Array<IssuesGridColumnSettingsName>
    order: number
}> = [
    {
        name: 'issueLabel',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: ['current', 'previous', 'future'],
        order: 0,
    },
    {
        name: 'date',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: ['current', 'previous', 'future'],
        order: 10,
    },
    {
        name: 'sector',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 20,
    },
    {
        name: 'marketplace',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 30,
    },
    {
        name: 'preMoney',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 40,
    },
    {
        name: 'subscriptionPrice',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 50,
    },
    {
        name: 'closePrice',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 60,
    },
    {
        name: 'turnover10d',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 70,
    },
    {
        name: 'marketcap',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 80,
    },
    {
        name: 'offeredSize',
        availableIn: ['current', 'future'],
        visibleIn: ['current', 'future'],
        order: 90,
    },
    {
        name: 'subscribedSize',
        availableIn: ['previous'],
        visibleIn: ['previous'],
        order: 100,
    },
    {
        name: 'financialAdvisors',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: ['current', 'previous', 'future'],
        order: 110,
    },
    {
        name: 'legalAdvisors',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 120,
    },
    {
        name: 'issuingAgents',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 130,
    },
    {
        name: 'totalCommitment',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: ['current', 'future'],
        order: 140,
    },
    {
        name: 'discount10dVwap',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 150,
    },
    {
        name: 'terpDiscount10dVwap',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 160,
    },
    {
        name: 'outcome',
        availableIn: ['previous'],
        visibleIn: ['previous'],
        order: 170,
    },
    {
        name: 'priceChange',
        availableIn: ['previous'],
        visibleIn: ['previous'],
        order: 180,
    },
    {
        name: 'dilutionMax',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 190,
    },
    {
        name: 'dilutionActual',
        availableIn: ['previous'],
        visibleIn: [],
        order: 200,
    },
    {
        name: 'feeLevel0',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 210,
    },
    {
        name: 'warrants',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 220,
    },
    {
        name: 'noncashConsideration',
        availableIn: ['current', 'previous', 'future'],
        visibleIn: [],
        order: 230,
    },
]

export const useIssueGridColumnSettings = () => {
    const { isLoading: isLoadingUser, updateMe, user } = useContext(UserMeContext)
    const visibleColumns = useMemo(
        () => ({
            future: {
                ...defaultValues
                    .filter((col) => col.availableIn.includes('future'))
                    .reduce(
                        (acc, col) => ({
                            ...acc,
                            [col.name]:
                                defaultValues.find((c) => c.name === col.name)?.visibleIn.includes('future') ?? false,
                        }),
                        {} as Record<IssueGridCols, boolean>,
                    ),
                ...user?.settings.issuesGrid.columnVisibility?.future,
            },
            current: {
                ...defaultValues
                    .filter((col) => col.availableIn.includes('current'))
                    .reduce(
                        (acc, col) => ({
                            ...acc,
                            [col.name]: defaultValues.find((c) => c.name === col.name)?.visibleIn.includes('current'),
                        }),
                        {} as Record<IssueGridCols, boolean>,
                    ),
                ...user?.settings.issuesGrid.columnVisibility?.current,
            },
            previous: {
                ...defaultValues
                    .filter((col) => col.availableIn.includes('previous'))
                    .reduce(
                        (acc, col) => ({
                            ...acc,
                            [col.name]: defaultValues.find((c) => c.name === col.name)?.visibleIn.includes('previous'),
                        }),
                        {} as Record<IssueGridCols, boolean>,
                    ),
                ...user?.settings.issuesGrid.columnVisibility?.previous,
            },
        }),
        [user?.settings.issuesGrid.columnVisibility],
    )
    const columnOrder = useMemo(() => {
        const sorter =
            (settingName: IssuesGridColumnSettingsName) =>
            (a: (typeof defaultValues)[number], b: (typeof defaultValues)[number]) => {
                const order = user?.settings.issuesGrid.columnOrder?.[settingName]

                if (!order) {
                    return a.order - b.order
                }

                return order.findIndex((col) => col === a.name) - order.findIndex((col) => col === b.name)
            }

        return {
            future: defaultValues.sort(sorter('future')).map((v) => v.name),
            current: defaultValues.sort(sorter('current')).map((v) => v.name),
            previous: defaultValues.sort(sorter('previous')).map((v) => v.name),
        }
    }, [user?.settings.issuesGrid.columnOrder])

    const handleChangeVisibleColumns = useCallback(
        (settings: Record<IssuesGridColumnSettingsName, Record<IssueGridCols, boolean>>) => {
            updateMe({ settings: { issuesGrid: { columnVisibility: settings } } }).catch((err) => console.error(err))
        },
        [updateMe],
    )

    const handleChangeColumnOrder = useCallback(
        (settings: Record<IssuesGridColumnSettingsName, Array<IssueGridCols>>) => {
            updateMe({ settings: { issuesGrid: { columnOrder: settings } } }).catch((err) => console.error(err))
        },
        [updateMe],
    )

    return useMemo(
        () => ({
            availableColumns: {
                future: defaultValues.filter((col) => col.availableIn.includes('future')).map((col) => col.name),
                current: defaultValues.filter((col) => col.availableIn.includes('current')).map((col) => col.name),
                previous: defaultValues.filter((col) => col.availableIn.includes('previous')).map((col) => col.name),
            },
            columnOrder,
            isLoading: isLoadingUser,
            onChangeColumnOrder: (settingName: IssuesGridColumnSettingsName, settings: Partial<Array<IssueGridCols>>) =>
                handleChangeColumnOrder({ ...columnOrder, [settingName]: settings }),
            onChangeVisibleColumns: (
                settingName: IssuesGridColumnSettingsName,
                settings: Partial<Record<IssueGridCols, boolean>>,
            ) => handleChangeVisibleColumns({ ...visibleColumns, [settingName]: settings }),
            visibleColumns,
        }),
        [columnOrder, handleChangeColumnOrder, handleChangeVisibleColumns, isLoadingUser, visibleColumns],
    )
}
